import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { Company } from 'garaje/graphql/generated/roomba-types';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type RoombaMetricsService from 'garaje/services/roomba-metrics';

type TimeFormat = 'STANDARD' | 'MILITARY';

export default class TimeDisplaySettings extends Component<{ company: Company }> {
  @service declare flashMessages: FlashMessagesService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare roombaMetrics: RoombaMetricsService;

  @tracked devicesTimeFormat = String(this.args.company.devicesTimeFormat) as TimeFormat;
  @tracked pendingTimeFormat: TimeFormat | null = null;

  get isDialogOpen(): boolean {
    return this.pendingTimeFormat !== null;
  }

  get pendingFormatLabel(): string {
    return this.pendingTimeFormat === 'MILITARY' ? 'military (24-hour)' : 'standard (12-hour)';
  }

  @action
  requestUpdate(value: TimeFormat, event: Event): void {
    // Defer all radio state to the bound `devicesTimeFormat`; without
    // preventDefault the browser's checked property sticks on the user's click
    // and falls out of sync with the binding when they cancel the confirm dialog.
    event.preventDefault();
    if (this.devicesTimeFormat === value || this.applyTimeFormat.isRunning) {
      return;
    }
    this.pendingTimeFormat = value;
  }

  @action
  cancelUpdate(): void {
    this.pendingTimeFormat = null;
  }

  applyTimeFormat = task({ drop: true }, async () => {
    const value = this.pendingTimeFormat;
    if (!value) {
      return;
    }
    this.roombaMetrics.trackEvent('devices_time_format_update', {
      time_format: value,
      company_id: this.args.company.id,
    });
    const initialTimeFormat = this.devicesTimeFormat;
    this.devicesTimeFormat = value;
    try {
      await this.roombaGraphql.updateCompanyConfig({
        devicesTimeFormat: value,
      });
    } catch (_e) {
      this.devicesTimeFormat = initialTimeFormat;
      this.flashMessages.showAndHideFlash('error', 'Error saving company settings');
      return;
    } finally {
      this.pendingTimeFormat = null;
    }
    this.flashMessages.showAndHideFlash('success', 'Saved!');
  });
}
